import { BUILDER_END } from './ends.ts';
import type { ProjectProcess, ProcessStep } from './graph.ts';
import { earlierPhase, type PhaseKey } from './phases.ts';

/**
 * Planning from the tree (process-flow spec, "Backward-chaining from landmark
 * events"). Each step has a length in calendar days; what it waits on gives
 * the order. A forward pass finds when each step can start and finish; a
 * backward pass from the goal finds how much each can slip, and the steps
 * with none are the critical path. The chain that drives the next landmark is
 * the one to show first: it is what moves the project forward now.
 *
 * Done steps sit on the day they were done; steps under way end no sooner
 * than tomorrow; a planned date for a gate or milestone holds it at that date
 * if the work would let it come sooner, a step that holds steps included (its
 * end is held). A step that holds steps is dated by them once it is under
 * way: it began no later than today and, done, ended when its last step did.
 */

export interface StepTiming {
  start: string;
  finish: string;
  /** Days it can slip without moving the end of the project. */
  slackDays: number;
  critical: boolean;
  /** On the chain that sets the next landmark's date. */
  drivesNext: boolean;
  /** A planned date the work cannot meet: the work runs to `finish`. */
  atRisk: boolean;
  /** For a step not yet started: the last day it can start without moving the end. */
  latestStart: string | null;
  /** The date planned for it, if one was recorded: what the client was told (the funnel's date). */
  plannedOn: string | null;
  /**
   * Its finish is a date someone set (Jason, 2026-10-06: "only dates someone set"): done, planned, or with a time on
   * the job and every wait before it dated too. Otherwise a step with no time counts as a week only to keep the order,
   * and its date is shown nowhere.
   */
  dated: boolean;
}

export interface Schedule {
  today: string;
  timing: Map<string, StepTiming>;
  /** The step the project ends with, and when. */
  end: { id: string; on: string } | null;
  /** The next gate or landmark not yet reached, by date. */
  next: { id: string; on: string } | null;
  /** The chain to the next landmark, earliest first: what moves the project forward now. */
  chainToNext: string[];
  /** The critical path to the end, earliest first. */
  critical: string[];
  /** Not started, and due to start within three weeks to hold the end date: the long leads to get ahead of. */
  startSoon: { id: string; by: string }[];
  /** Each step's place in the order of work: ties on a date read in this order. */
  rank: Map<string, number>;
}

export const DEFAULT_DAYS = 7;
const DAY = 86_400_000;
const toN = (d: string) => Math.round(Date.parse(`${d}T00:00:00Z`) / DAY);
const toD = (n: number) => new Date(n * DAY).toISOString().slice(0, 10);
/** No phases given: every task after its step's start, as the order read before a task could be done early. */
const NO_PHASES: ReadonlyMap<string, PhaseKey> = new Map();

function lengthOf(s: ProcessStep): number {
  if (s.days !== null) return s.days;
  // A gate or landmark is a point; an inspection is a visit; work takes a week until someone says otherwise.
  return s.kind === 'milestone' || s.kind === 'gate' ? 0 : s.kind === 'inspection' ? 1 : DEFAULT_DAYS;
}

/**
 * The order of work as one graph, the schedule's: the live steps, a step that
 * holds steps as a start and an end around them, and each wait from the end of
 * what is waited on to the start of what waits. A task done in an earlier phase
 * than the step holding it (design study 31, slice 4), as the job's `phases`
 * read (core `projectPhases`), comes before its step's end, never after its
 * start: it is worked in its own phase, free of what its step waits on.
 */
function orderGraph(p: ProjectProcess, phases: ReadonlyMap<string, PhaseKey>) {
  const live = p.steps.filter((s) => !s.off);
  const byId = new Map(live.map((s) => [s.id, s]));
  const kids = new Map<string, ProcessStep[]>();
  for (const s of live) if (s.parentId && byId.has(s.parentId)) kids.set(s.parentId, [...(kids.get(s.parentId) ?? []), s]);
  const isGroup = (id: string) => (kids.get(id)?.length ?? 0) > 0;
  const entry = (id: string) => (isGroup(id) ? `${id}:start` : id);
  const exit = (id: string) => (isGroup(id) ? `${id}:end` : id);
  const nodes = new Map<string, { dur: number; step: ProcessStep | null; group: ProcessStep | null; end: boolean }>();
  const preds = new Map<string, Set<string>>();
  const succ = new Map<string, Set<string>>();
  const edge = (a: string, b: string) => {
    if (a === b) return;
    preds.get(b)!.add(a);
    succ.get(a)!.add(b);
  };
  for (const s of live) {
    const ids = isGroup(s.id) ? [`${s.id}:start`, `${s.id}:end`] : [s.id];
    for (const id of ids) {
      const group = isGroup(s.id);
      nodes.set(id, { dur: group ? 0 : lengthOf(s), step: group ? null : s, group: group ? s : null, end: id.endsWith(':end') });
      preds.set(id, new Set());
      succ.set(id, new Set());
    }
  }
  for (const s of live) {
    if (isGroup(s.id)) {
      for (const c of kids.get(s.id)!) {
        if (!earlierPhase(phases, c.id, s.id)) edge(`${s.id}:start`, entry(c.id));
        edge(exit(c.id), `${s.id}:end`);
      }
    }
  }
  for (const l of p.links) {
    if (!byId.has(l.stepId) || !byId.has(l.needsId)) continue;
    edge(exit(l.needsId), entry(l.stepId));
  }
  return { live, byId, isGroup, entry, exit, nodes, preds, succ };
}

/**
 * What a step's finish can push later: every live step whose end comes after
 * it in the order of work, what waits on the steps that hold it included (a
 * landmark inside an order moves what waits on the order). `phases` as the
 * schedule reads them.
 */
export function downstream(p: ProjectProcess, stepId: string, phases: ReadonlyMap<string, PhaseKey> = NO_PHASES): Set<string> {
  const g = orderGraph(p, phases);
  const out = new Set<string>();
  if (!g.byId.has(stepId)) return out;
  const seen = new Set<string>();
  const stack = [g.exit(stepId)];
  while (stack.length) {
    const k = stack.pop()!;
    if (seen.has(k)) continue;
    seen.add(k);
    for (const m of g.succ.get(k)!) stack.push(m);
  }
  for (const s of g.live) if (s.id !== stepId && seen.has(g.exit(s.id))) out.add(s.id);
  return out;
}

/**
 * The plan of a job from `today`, with the dates planned for its gates and milestones (`plans`). `phases`, the job's
 * (core `projectPhases`): a task done early is planned in its own phase, not after its step's start (design study 31,
 * slice 4); left out, every task is planned inside its step.
 */
export function schedule(p: ProjectProcess, today: string, plans: ReadonlyMap<string, string> = new Map(), phases: ReadonlyMap<string, PhaseKey> = NO_PHASES): Schedule {
  const t0 = toN(today);
  // A planned date is a gate's or a milestone's (design study 31, Settled 3: a milestone does what a landmark did); one
  // kept on a step of work from before moves nothing, its row kept.
  const kinds = new Map(p.steps.map((s) => [s.id, s.kind]));
  const pinned: ReadonlyMap<string, string> = new Map([...plans].filter(([id]) => kinds.get(id) === 'gate' || kinds.get(id) === 'milestone'));
  // The graph to plan over: a group is a start and an end around its steps.
  const { live, byId, isGroup, entry, exit, nodes, preds, succ } = orderGraph(p, phases);

  // Topological order (Kahn); anything caught in a loop is planned with its remaining waits ignored.
  const indeg = new Map([...nodes.keys()].map((k) => [k, preds.get(k)!.size]));
  const queue = [...nodes.keys()].filter((k) => indeg.get(k) === 0);
  const order: string[] = [];
  const placed = new Set<string>();
  while (order.length < nodes.size) {
    if (!queue.length) {
      const stuck = [...nodes.keys()].find((k) => !placed.has(k))!;
      queue.push(stuck);
      indeg.set(stuck, 0);
    }
    const k = queue.shift()!;
    if (placed.has(k)) continue;
    placed.add(k);
    order.push(k);
    for (const m of succ.get(k)!) {
      indeg.set(m, indeg.get(m)! - 1);
      if (indeg.get(m) === 0) queue.push(m);
    }
  }

  // Forward: earliest start and finish.
  const es = new Map<string, number>();
  const ef = new Map<string, number>();
  const risk = new Set<string>();
  for (const k of order) {
    const n = nodes.get(k)!;
    const after = Math.max(-Infinity, ...[...preds.get(k)!].filter((q) => ef.has(q)).map((q) => ef.get(q)!));
    const s = n.step;
    const g = n.group;
    let start: number;
    let finish: number;
    if (s && s.state === 'done') {
      finish = s.doneOn ? toN(s.doneOn) : Math.min(t0, Number.isFinite(after) ? after + n.dur : t0);
      start = s.startedOn ? Math.min(toN(s.startedOn), finish) : finish - n.dur;
    } else if (s && s.state === 'in_progress') {
      start = s.startedOn ? toN(s.startedOn) : t0;
      finish = Math.max(t0 + 1, start + n.dur);
    } else if (g && (g.state === 'done' || (g.state === 'in_progress' && !n.end))) {
      // Under way, it began no later than today; done, it ended when its last step did (its own day if they have none).
      start = finish = n.end
        ? Number.isFinite(after) ? after : g.doneOn ? toN(g.doneOn) : t0
        : Math.min(t0, Number.isFinite(after) ? after : t0, g.startedOn ? toN(g.startedOn) : t0);
    } else {
      start = Math.max(t0, Number.isFinite(after) ? after : t0);
      finish = start + n.dur;
      // A group's plan holds its end: the steps inside it run to it.
      const planned = s ? pinned.get(s.id) : g && n.end ? pinned.get(g.id) : undefined;
      if (planned !== undefined) {
        const pn = toN(planned);
        if (pn >= finish) {
          finish = pn;
          start = Math.max(start, pn - n.dur);
        } else risk.add(k);
      }
    }
    // A group's start is when its first step can start: what it waits on, or today.
    es.set(k, start);
    ef.set(k, finish);
  }

  // Which dates are ones someone set: done work, a planned date, or a time on the job with every wait before it dated.
  const dated = new Map<string, boolean>();
  for (const k of order) {
    const n = nodes.get(k)!;
    const waits = [...preds.get(k)!].every((q) => dated.get(q) === true);
    const s = n.step;
    const g = n.group;
    let d: boolean;
    if (s) {
      if (s.state === 'done' || pinned.has(s.id)) d = true;
      else if (s.state === 'in_progress') d = s.days !== null;
      else d = waits && (s.days !== null || s.kind === 'gate' || s.kind === 'milestone');
    } else d = g!.state === 'done' || (n.end && pinned.has(g!.id)) || waits;
    dated.set(k, d);
  }

  // The goal: the latest finish of anything. Backward from it: latest finish and slack.
  let endKey: string | null = null;
  for (const k of order) if (endKey === null || ef.get(k)! >= ef.get(endKey)!) endKey = k;
  const lf = new Map<string, number>();
  const ls = new Map<string, number>();
  const horizon = endKey ? ef.get(endKey)! : t0;
  for (let i = order.length - 1; i >= 0; i--) {
    const k = order[i]!;
    const next = [...succ.get(k)!].filter((m) => ls.has(m));
    const latest = next.length ? Math.min(...next.map((m) => ls.get(m)!)) : horizon;
    lf.set(k, latest);
    ls.set(k, latest - nodes.get(k)!.dur);
  }

  const isDone = (k: string) => {
    const s = nodes.get(k)!.step;
    if (s) return s.state === 'done';
    const id = k.replace(/:(start|end)$/, '');
    return byId.get(id)!.state === 'done';
  };

  // The next gate or milestone not yet reached, and the chain driving it (a milestone does what a landmark did, design
  // study 31, Settled 3); never Project Complete, the job's end.
  let next: { id: string; on: string } | null = null;
  for (const s of live) {
    if (s.state === 'done' || !(s.kind === 'gate' || s.kind === 'milestone') || s.key === BUILDER_END) continue;
    const f = ef.get(exit(s.id));
    if (f === undefined) continue;
    if (!next || f < toN(next.on) || (f === toN(next.on) && order.indexOf(exit(s.id)) < order.indexOf(exit(next.id)))) next = { id: s.id, on: toD(f) };
  }
  // What the client was told stands: the next stop reads on its planned date when it has one, and a planned date
  // gone by without it is today (the funnel's rule).
  if (next && pinned.has(next.id)) next = { id: next.id, on: toD(Math.max(toN(pinned.get(next.id)!), t0)) };
  const drives = new Set<string>();
  if (next) {
    // Walk back along the waits that set each date (the latest-finishing one), until done work.
    const stack = [exit(next.id)];
    while (stack.length) {
      const k = stack.pop()!;
      if (drives.has(k)) continue;
      drives.add(k);
      const ps = [...preds.get(k)!].filter((q) => !isDone(q));
      if (!ps.length) continue;
      const latest = Math.max(...ps.map((q) => ef.get(q)!));
      for (const q of ps) if (ef.get(q) === latest) stack.push(q);
    }
  }

  const timing = new Map<string, StepTiming>();
  for (const s of live) {
    const a = entry(s.id);
    const b = exit(s.id);
    const slack = Math.min(ls.get(a)! - es.get(a)!, lf.get(b)! - ef.get(b)!);
    const done = s.state === 'done';
    timing.set(s.id, {
      // A group's steps may be dated before what it waited on finished: it starts no later than it ends.
      start: toD(Math.min(es.get(a)!, ef.get(b)!)),
      finish: toD(ef.get(b)!),
      slackDays: done ? 0 : Math.max(0, slack),
      critical: !done && slack <= 0,
      drivesNext: !done && (drives.has(a) || drives.has(b)),
      atRisk: risk.has(b),
      latestStart: done || s.state === 'in_progress' ? null : toD(ls.get(a)!),
      plannedOn: done ? null : (pinned.get(s.id) ?? null),
      dated: dated.get(b) ?? false,
    });
  }

  const pos = new Map(order.map((k, i) => [k, i]));
  const byStart = (ids: string[]) =>
    ids.sort(
      (x, y) =>
        timing.get(x)!.start.localeCompare(timing.get(y)!.start) ||
        timing.get(x)!.finish.localeCompare(timing.get(y)!.finish) ||
        pos.get(exit(x))! - pos.get(exit(y))!,
    );
  const critical = byStart(live.filter((s) => timing.get(s.id)!.critical && !isGroup(s.id)).map((s) => s.id));
  const chainToNext = byStart(live.filter((s) => timing.get(s.id)!.drivesNext && !isGroup(s.id)).map((s) => s.id));
  // To hold an end date someone set, never one the schedule guessed.
  const startSoon = (endKey !== null && dated.get(endKey) ? live : [])
    .filter((s) => s.state === 'not_started' || s.state === 'available')
    .filter((s) => !isGroup(s.id) && s.kind !== 'gate' && s.kind !== 'milestone')
    .map((s) => ({ id: s.id, by: timing.get(s.id)!.latestStart! }))
    .filter((x) => x.by !== null && toN(x.by) <= t0 + 21 && toN(x.by) >= t0 - 365)
    .sort((a, b) => a.by.localeCompare(b.by));

  const endStep = endKey ? endKey.replace(/:(start|end)$/, '') : null;
  return {
    today,
    rank: new Map(live.map((s) => [s.id, pos.get(exit(s.id))!])),
    timing,
    end: endStep ? { id: endStep, on: toD(horizon) } : null,
    next,
    chainToNext,
    critical,
    startSoon,
  };
}
